import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { downloadCsv } from '@/utils/downloadFile';
import { DeleteButton } from '@/components/DeleteConfirmDialog';
import { 
  Clock, 
  Play, 
  Square, 
  Plus, 
  Download, 
  Edit2,
  Car
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Kategorien
const ADMIN_CATEGORIES = ["Administration / Büro", "Werkstatt", "Automatenservice", "Sonstiges"];
const HELPER_CATEGORIES = ["Werkstatt"];

const TimeTrackingPage = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const categories = isAdmin ? ADMIN_CATEGORIES : HELPER_CATEGORIES;

  // State
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalHours, setTotalHours] = useState(0);
  const [totalKm, setTotalKm] = useState(0);
  
  // Filter
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [users, setUsers] = useState([]);
  
  // Dialog State
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    start_time: '',
    end_time: '',
    category: 'Werkstatt',
    km_driven: '',
    notes: ''
  });
  
  // Offener Eintrag (für Kommen/Gehen)
  const [openEntry, setOpenEntry] = useState(null);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const timerRef = useRef(null);

  // Fetch active entry
  const fetchActiveEntry = async () => {
    try {
      const res = await axios.get(`${API}/time-entries/active`);
      const active = res.data?.active;
      setOpenEntry(active || null);
      
      if (active) {
        startTimer(active.start_time);
      } else {
        stopTimer();
      }
    } catch (error) {
      console.error('Fehler beim Laden des aktiven Eintrags:', error);
      setOpenEntry(null);
      stopTimer();
    }
  };

  // Timer für Laufzeit
  const startTimer = (startTime) => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    const updateElapsed = () => {
      const [hours, minutes] = startTime.split(':').map(Number);
      const start = new Date();
      start.setHours(hours, minutes, 0, 0);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      
      if (diff < 0) {
        setElapsedTime('00:00:00');
        return;
      }
      
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsedTime(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    
    updateElapsed();
    timerRef.current = setInterval(updateElapsed, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setElapsedTime('00:00:00');
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Fetch entries
  const fetchEntries = async () => {
    setLoading(true);
    try {
      let url = `${API}/time-entries?month=${selectedMonth}`;
      if (isAdmin && selectedUser) url += `&user_id=${selectedUser}`;
      if (selectedCategory) url += `&category=${encodeURIComponent(selectedCategory)}`;
      
      const res = await axios.get(url);
      setEntries(res.data.entries || []);
      setTotalHours(res.data.total_hours || 0);
      setTotalKm(res.data.total_km || 0);
    } catch (error) {
      // Nur bei echten Serverfehlern einen Toast zeigen
      if (error.response?.status >= 500) {
        toast.error('Serverfehler beim Laden der Zeiterfassung');
      } else if (error.response?.status === 403) {
        toast.error('Keine Berechtigung für Zeiterfassung');
      }
      setEntries([]);
      setTotalHours(0);
      setTotalKm(0);
      console.error('Zeiterfassung laden:', error.response?.status || error.message);
    }
    setLoading(false);
  };

  // Fetch users (nur Admin)
  const fetchUsers = async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer');
    }
  };

  useEffect(() => {
    fetchActiveEntry();
    fetchEntries();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [selectedMonth, selectedUser, selectedCategory]);

  // Kommen
  const handleClockIn = async () => {
    try {
      const res = await axios.post(`${API}/time-entries/clock-in?category=${encodeURIComponent(formData.category)}`);
      toast.success('Arbeitszeit gestartet');
      setOpenEntry(res.data);
      startTimer(res.data.start_time);
      fetchEntries();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'Es existiert bereits ein offener Eintrag für heute') {
        toast.error('Du hast bereits eine aktive Arbeitszeit');
        fetchActiveEntry();
      } else {
        toast.error(detail || 'Fehler beim Starten');
      }
    }
  };

  // Gehen
  const handleClockOut = async () => {
    try {
      let url = `${API}/time-entries/clock-out`;
      const params = new URLSearchParams();
      
      if (isAdmin && formData.km_driven) {
        params.append('km_driven', formData.km_driven);
      }
      if (formData.notes) {
        params.append('notes', formData.notes);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      await axios.post(url);
      toast.success('Arbeitszeit beendet');
      setOpenEntry(null);
      stopTimer();
      setFormData(prev => ({ ...prev, km_driven: '', notes: '' }));
      fetchEntries();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'Kein offener Eintrag für heute gefunden') {
        toast.error('Keine aktive Arbeitszeit gefunden');
        fetchActiveEntry();
      } else {
        toast.error(detail || 'Fehler beim Beenden');
      }
    }
  };

  // Erstellen
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        entry_date: formData.entry_date,
        start_time: formData.start_time,
        end_time: formData.end_time || null,
        category: formData.category,
        notes: formData.notes || null
      };
      if (isAdmin && formData.km_driven) {
        data.km_driven = parseFloat(formData.km_driven);
      }
      
      await axios.post(`${API}/time-entries`, data);
      toast.success('Eintrag erstellt');
      setShowCreateDialog(false);
      resetForm();
      fetchEntries();
      fetchActiveEntry();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  // Bearbeiten
  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        entry_date: formData.entry_date,
        start_time: formData.start_time,
        end_time: formData.end_time || null,
        category: formData.category,
        notes: formData.notes || null
      };
      if (isAdmin && formData.km_driven !== '') {
        data.km_driven = parseFloat(formData.km_driven);
      }
      
      await axios.put(`${API}/time-entries/${editingEntry.id}`, data);
      toast.success('Eintrag aktualisiert');
      setEditingEntry(null);
      resetForm();
      fetchEntries();
      fetchActiveEntry();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  // Löschen - verwendet DeleteConfirmDialog Komponente
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/time-entries/${id}`);
      toast.success('Eintrag gelöscht');
      fetchEntries();
      fetchActiveEntry();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
      throw error; // Re-throw für Dialog
    }
  };

  // CSV Export - nur für Admin, mit zentraler Download-Utility
  const handleExport = async () => {
    let url = `${API}/time-entries/export?month=${selectedMonth}`;
    if (selectedUser) url += `&user_id=${selectedUser}`;
    if (selectedCategory) url += `&category=${encodeURIComponent(selectedCategory)}`;
    
    const filename = `timesheet_${selectedMonth.replace('-', '_')}.csv`;
    
    // Verwende axios aus dem globalen Kontext (hat Auth-Interceptor)
    const result = await downloadCsv(axios, url, filename);
    
    if (result.success) {
      toast.success('CSV exportiert');
    } else {
      toast.error(result.error || 'Fehler beim Exportieren');
    }
  };

  const resetForm = () => {
    setFormData({
      entry_date: new Date().toISOString().split('T')[0],
      start_time: '',
      end_time: '',
      category: 'Werkstatt',
      km_driven: '',
      notes: ''
    });
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      entry_date: entry.entry_date,
      start_time: entry.start_time,
      end_time: entry.end_time || '',
      category: entry.category,
      km_driven: entry.km_driven?.toString() || '',
      notes: entry.notes || ''
    });
  };

  // Prüfe ob Helfer Eintrag bearbeiten darf (nur heute)
  const canEdit = (entry) => {
    if (isAdmin) return true;
    const today = new Date().toISOString().split('T')[0];
    return entry.entry_date === today && entry.user_id === user?.id;
  };

  // Dauer berechnen
  const calculateDuration = (start, end) => {
    if (!end) return 'offen';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const hours = (eh * 60 + em - sh * 60 - sm) / 60;
    return `${hours.toFixed(2)} h`;
  };

  // Monatsliste für Dropdown
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  return (
    <div className="space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            Zeiterfassung
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Arbeitszeiten {isAdmin ? 'aller Mitarbeiter' : ''} erfassen und auswerten
          </p>
        </div>
        <div className="flex gap-2">
          {/* CSV Export nur für Admin */}
          {isAdmin && (
            <Button onClick={handleExport} variant="outline" size="sm">
              <Download size={16} className="mr-2" />
              CSV Export
            </Button>
          )}
          <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
            <Plus size={16} className="mr-2" />
            Neuer Eintrag
          </Button>
        </div>
      </div>

      {/* Kommen/Gehen Karte */}
      <Card className={`p-6 ${openEntry ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'}`}>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${openEntry ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`}>
              <Clock size={32} className="text-white" />
            </div>
            <div>
              {openEntry ? (
                <>
                  <h2 className="text-lg font-semibold text-green-700">
                    Aktive Arbeitszeit seit {openEntry.start_time} Uhr
                  </h2>
                  <p className="text-2xl font-mono font-bold text-green-600">
                    {elapsedTime}
                  </p>
                </>
              ) : (
                <h2 className="text-lg font-semibold text-slate-600">
                  Keine aktive Arbeitszeit
                </h2>
              )}
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {!openEntry ? (
              <>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Kategorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleClockIn} className="bg-green-600 hover:bg-green-700">
                  <Play size={16} className="mr-2" />
                  Kommen
                </Button>
              </>
            ) : (
              <>
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <Car size={16} className="text-slate-500" />
                    <Input
                      type="number"
                      placeholder="KM"
                      value={formData.km_driven}
                      onChange={(e) => setFormData({...formData, km_driven: e.target.value})}
                      className="w-24"
                    />
                  </div>
                )}
                <Button onClick={handleClockOut} className="bg-red-600 hover:bg-red-700">
                  <Square size={16} className="mr-2" />
                  Gehen
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Filter */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Label className="text-sm text-slate-500">Monat</Label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getMonthOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {isAdmin && (
            <div className="flex-1">
              <Label className="text-sm text-slate-500">Mitarbeiter</Label>
              <Select value={selectedUser || 'all'} onValueChange={(v) => setSelectedUser(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Alle Mitarbeiter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Mitarbeiter</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="flex-1">
            <Label className="text-sm text-slate-500">Kategorie</Label>
            <Select value={selectedCategory || 'all'} onValueChange={(v) => setSelectedCategory(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="Alle Kategorien" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kategorien</SelectItem>
                {ADMIN_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Zusammenfassung - OHNE Auto geschlossen */}
      <div className={`grid ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'} gap-4`}>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{entries.length}</p>
          <p className="text-sm text-slate-500">Einträge</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{totalHours.toFixed(2)} h</p>
          <p className="text-sm text-slate-500">Gesamtstunden</p>
        </Card>
        {isAdmin && (
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{totalKm.toFixed(1)} km</p>
            <p className="text-sm text-slate-500">Gefahrene KM</p>
          </Card>
        )}
      </div>

      {/* Tabelle - OHNE Auto geschlossen Spalte */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                {isAdmin && <th className="text-left py-3 px-4 font-semibold">Mitarbeiter</th>}
                <th className="text-left py-3 px-4 font-semibold">Datum</th>
                <th className="text-left py-3 px-4 font-semibold">Start</th>
                <th className="text-left py-3 px-4 font-semibold">Ende</th>
                <th className="text-left py-3 px-4 font-semibold">Dauer</th>
                <th className="text-left py-3 px-4 font-semibold">Kategorie</th>
                {isAdmin && <th className="text-left py-3 px-4 font-semibold">KM</th>}
                <th className="text-right py-3 px-4 font-semibold">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isAdmin ? 8 : 6} className="text-center py-8 text-slate-500">
                    Laden...
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 8 : 6} className="text-center py-8 text-slate-500">
                    Keine Einträge für diesen Zeitraum
                  </td>
                </tr>
              ) : (
                entries.map(entry => (
                  <tr key={entry.id} className={`border-t hover:bg-slate-50 ${!entry.end_time ? 'bg-green-50' : ''}`}>
                    {isAdmin && (
                      <td className="py-3 px-4">{entry.user_name_snapshot}</td>
                    )}
                    <td className="py-3 px-4">
                      {new Date(entry.entry_date).toLocaleDateString('de-DE')}
                    </td>
                    <td className="py-3 px-4">{entry.start_time}</td>
                    <td className="py-3 px-4">
                      {entry.end_time || <span className="text-green-600 font-medium">läuft...</span>}
                    </td>
                    <td className="py-3 px-4">{calculateDuration(entry.start_time, entry.end_time)}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                        {entry.category}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="py-3 px-4">
                        {entry.km_driven ? `${entry.km_driven} km` : '-'}
                      </td>
                    )}
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end gap-2">
                        {canEdit(entry) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(entry)}
                          >
                            <Edit2 size={14} />
                          </Button>
                        )}
                        {isAdmin && (
                          <DeleteButton
                            onDelete={() => handleDelete(entry.id)}
                            title="Zeiteintrag löschen"
                            itemName={`${entry.entry_date} ${entry.start_time}`}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || !!editingEntry} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setEditingEntry(null);
          resetForm();
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Eintrag bearbeiten' : 'Neuer Zeiteintrag'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={editingEntry ? handleUpdate : handleCreate} className="space-y-4">
            <div>
              <Label>Datum</Label>
              <Input
                type="date"
                value={formData.entry_date}
                onChange={(e) => setFormData({...formData, entry_date: e.target.value})}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Startzeit</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Endzeit</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Kategorie</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {isAdmin && (
              <div>
                <Label>Gefahrene KM (optional)</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.km_driven}
                  onChange={(e) => setFormData({...formData, km_driven: e.target.value})}
                  placeholder="z.B. 25.5"
                />
              </div>
            )}
            
            <div>
              <Label>Notizen (optional)</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Kurze Beschreibung"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowCreateDialog(false);
                setEditingEntry(null);
                resetForm();
              }}>
                Abbrechen
              </Button>
              <Button type="submit">
                {editingEntry ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeTrackingPage;
